/**
 * №13.1 §2 / №13.2 — contact identity block for {@link CreateEntityModal}.
 *
 * Only mounted when the modal's entity type is `contact` (slug === "contact").
 * kind is person | organisation (§13.2 rev B).
 * Owns the three client-form concerns the generic field loop can't express:
 *
 *   §2.1 (M8–M11) kind-driven name — an Individual/Organisation toggle that
 *        swaps the name fields. Individual asks Given + Family (both required in
 *        the form) and derives `name` = "Given Family" as a read-only preview;
 *        Organisation asks a single Organisation-name input that *is* `name`.
 *        The two shapes are never shown together.
 *
 *   §5 (M14/M15) search-before-create — as the org-name / family-name is typed,
 *        a debounced advisory "Did you mean…" panel lists existing `client`
 *        matches (name + disambiguator), each a link to that client's record.
 *        It never blocks submit (Create anyway stays enabled in the parent).
 *
 * State lives in the parent's `values` map (via {@link setValue}); this component
 * is presentational + the search query. Reads/writes:
 *   - values.kind          "person" | "organisation"
 *   - values.given_name    (individual)
 *   - values.family_name   (individual)
 *   - values.name          (organisation — the org name is `name`)
 */
import { useMemo } from "react";
import { Link } from "react-router";
import { useEntities } from "../../../lib/hooks/use-kuvio";
import { useDebouncedValue } from "../../../hooks/use-debounced-value";
import type { EntityRecord, EntityTypeDefinition } from "../../../lib/types";

const MAX_SUGGESTIONS = 6;

const labelStyle: React.CSSProperties = { display: "flex", flexDirection: "column", gap: 4, fontSize: 12.5 };
const labelTextStyle: React.CSSProperties = { color: "var(--color-text-secondary)" };

function capitalise(s: string): string {
  return s ? s.charAt(0).toUpperCase() + s.slice(1) : s;
}

/** A short human disambiguator for a candidate client row (kind · contact/id). */
function disambiguatorFor(row: Record<string, unknown>): string {
  const kind = row.kind ? capitalise(String(row.kind)) : "Client";
  const detail = [row.email, row.abn, row.phone, row.address]
    .map((v) => (v == null ? "" : String(v).trim()))
    .find((v) => v.length > 0);
  return detail ? `${kind} · ${detail}` : kind;
}

export function ClientCreateFields({
  values,
  setValue,
  clientType,
  editingId,
}: {
  values: Record<string, string>;
  setValue: (name: string, value: string) => void;
  clientType: EntityTypeDefinition;
  /** In edit mode, exclude the record being edited from the "Did you mean" list. */
  editingId?: string;
}) {
  // №13.3 §2 — the kind toggle's VALUES + default derive from the `kind` enum
  // field on the contact type, never hardcoded. The person shape (given/family
  // parts) is the field default (or first choice); the org shape (single name) is
  // the other. A renamed/added kind can no longer drift the toggle out of the enum.
  const kindField = clientType.fields.find((f) => f.name === "kind");
  const kindChoices = kindField?.choices ?? [];
  const personKind = String(kindField?.default ?? kindChoices[0] ?? "person");
  const orgKind = kindChoices.find((c) => c !== personKind) ?? "organisation";
  const toggleChoices = kindChoices.length > 0 ? kindChoices : [personKind, orgKind];

  const kind = values.kind === orgKind ? orgKind : personKind;
  const given = values.given_name ?? "";
  const family = values.family_name ?? "";
  const orgName = values.name ?? "";
  const derivedName = `${given.trim()} ${family.trim()}`.trim();

  // §5 — search term follows the "identifying" name field for the current kind:
  // the family name for a person, the org name for an organisation.
  const term = (kind === "person" ? family : orgName).trim();
  const debounced = useDebouncedValue(term, 300);

  // №13.2 — dedupe against existing contacts (the one master).
  const clientsQuery = useEntities(clientType);

  const matches = useMemo(() => {
    const q = debounced.toLowerCase();
    if (q.length < 2) return [];
    const rows = (clientsQuery.data?.entities ?? []) as EntityRecord[];
    return rows
      .filter((r) => r.id !== editingId)
      .filter((r) => String(r.name ?? "").toLowerCase().includes(q))
      .slice(0, MAX_SUGGESTIONS);
  }, [debounced, clientsQuery.data, editingId]);

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
      {/* M8 — kind toggle (Individual / Organisation), default Individual. */}
      <div style={labelStyle}>
        <span style={labelTextStyle}>Type *</span>
        <div className="row" role="radiogroup" aria-label="Contact type" style={{ gap: 8 }}>
          {toggleChoices.map((k) => (
            <button
              key={k}
              type="button"
              role="radio"
              aria-checked={kind === k}
              data-variant={kind === k ? "primary" : undefined}
              onClick={() => setValue("kind", k)}
              style={{ flex: 1, textTransform: "capitalize" }}
            >
              {k}
            </button>
          ))}
        </div>
      </div>

      {kind === personKind ? (
        <>
          {/* M9 — Given + Family (both required in the form); `name` is derived. */}
          <label style={labelStyle}>
            <span style={labelTextStyle}>Given name *</span>
            <input
              className="cem-input"
              type="text"
              value={given}
              onChange={(e) => setValue("given_name", e.target.value)}
            />
          </label>
          <label style={labelStyle}>
            <span style={labelTextStyle}>Family name *</span>
            <input
              className="cem-input"
              type="text"
              value={family}
              onChange={(e) => setValue("family_name", e.target.value)}
            />
          </label>
          <div style={labelStyle}>
            <span style={labelTextStyle}>Full name (auto)</span>
            <div
              className="cem-input"
              aria-readonly="true"
              style={{ color: derivedName ? "var(--color-text-primary)" : "var(--color-text-tertiary)", background: "var(--color-background-secondary)" }}
            >
              {derivedName || "Given Family"}
            </div>
          </div>
        </>
      ) : (
        // M10 — a single Organisation-name input, which *is* `name`.
        <label style={labelStyle}>
          <span style={labelTextStyle}>Organisation name *</span>
          <input
            className="cem-input"
            type="text"
            value={orgName}
            onChange={(e) => setValue("name", e.target.value)}
          />
        </label>
      )}

      {/* M14/M15 — advisory "Did you mean…" panel; never blocks submit. */}
      {matches.length > 0 && (
        <div
          className="stack"
          style={{
            border: "0.5px solid var(--color-border-secondary)",
            borderRadius: "var(--border-radius-md)",
            padding: 10,
            background: "var(--color-background-secondary)",
            gap: 6,
          }}
        >
          <div style={{ fontSize: 11.5, color: "var(--color-text-secondary)" }}>
            Did you mean an existing contact?
          </div>
          {matches.map((m) => (
            <Link
              key={m.id}
              to={`/app/place/contact/${m.id}`}
              className="kuvio-panel-row"
              style={{
                display: "flex",
                alignItems: "baseline",
                gap: 8,
                padding: "5px 8px",
                borderRadius: "var(--border-radius-sm)",
                textDecoration: "none",
                color: "var(--color-text-primary)",
              }}
            >
              <span style={{ fontSize: 13, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                {String(m.name ?? m.id)}
              </span>
              <span style={{ marginLeft: "auto", fontSize: 11, color: "var(--color-text-tertiary)", flexShrink: 0 }}>
                {disambiguatorFor(m)}
              </span>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
